import { configureStore } from '@reduxjs/toolkit';
import { combineReducers } from 'redux';
import { FLUSH, PAUSE, PERSIST, PURGE, REGISTER, REHYDRATE, persistReducer, persistStore } from 'redux-persist';
import autoMergeLevel2 from 'redux-persist/lib/stateReconciler/autoMergeLevel2';
import storage from 'redux-persist/lib/storage';

import { ENV_IS_TESTING, isApplicationKiosk } from '@lib/core/service/consts';
import { analyticsMiddleware } from '@lib/core/service/middleware/analyticsMiddleware';
import Reducers from '@lib/core/service/rootReducer';
import { initialMockedState } from '@lib/core/tests/consts';

export const Reducer = combineReducers({
  ...Reducers,
  banners: persistReducer<ReturnType<typeof Reducers.banners>>(
    {
      key: 'banners',
      stateReconciler: autoMergeLevel2,
      storage,
      timeout: 50,
      whitelist: ['isDiscountVoucherShown'],
    },
    Reducers.banners,
  ),
  clickTracker: persistReducer<ReturnType<typeof Reducers.clickTracker>>(
    {
      key: 'clickTracker',
      stateReconciler: autoMergeLevel2,
      storage,
      timeout: 50,
      whitelist: ['productsFindMeClickTimes'],
    },
    Reducers.clickTracker,
  ),
  gamification: persistReducer<ReturnType<typeof Reducers.gamification>>(
    {
      key: 'gamification',
      stateReconciler: autoMergeLevel2,
      storage,
      timeout: 50,
      whitelist: ['gamesProgress', 'leaderboardConfirmationByGame', 'surveyGame'],
    },
    Reducers.gamification,
  ),
  productFeedback: persistReducer<ReturnType<typeof Reducers.productFeedback>>(
    {
      key: 'productFeedback',
      stateReconciler: autoMergeLevel2,
      storage,
      timeout: 50,
    },
    Reducers.productFeedback,
  ),
  productTip: persistReducer<ReturnType<typeof Reducers.productTip>>(
    {
      key: 'productTip',
      stateReconciler: autoMergeLevel2,
      storage,
      timeout: 50,
      whitelist: ['usersProductTipsStorage'],
    },
    Reducers.productTip,
  ),
  quizView: persistReducer<ReturnType<typeof Reducers.quizView>>(
    {
      key: 'quizView',
      stateReconciler: autoMergeLevel2,
      storage,
      timeout: 50,
      whitelist: ['answerTags'],
    },
    Reducers.quizView,
  ),
  remoteAccess: persistReducer<ReturnType<typeof Reducers.remoteAccess>>(
    {
      key: 'remoteAccess',
      stateReconciler: autoMergeLevel2,
      storage,
      timeout: 50,
    },
    Reducers.remoteAccess,
  ),
  routes: persistReducer<ReturnType<typeof Reducers.routes>>(
    {
      key: 'basename',
      stateReconciler: autoMergeLevel2,
      storage,
      timeout: 50,
      whitelist: ['basename'],
    },
    Reducers.routes,
  ),
  service: persistReducer<ReturnType<typeof Reducers.service>>(
    {
      key: 'service',
      stateReconciler: autoMergeLevel2,
      storage,
      timeout: 50,
      whitelist: [
        'isDevToolsEnabled',
        'isTasteIdGuideShown',
        'shouldHideDownloadAppCard',
        'isTastePathOnboardingShown',
        'isFunctionalityExplanationDisplayed',
        'isFooterTutorialDisplayed',
        'isFirstTimeCatalogShowed',
        'isHostMocked',
      ],
    },
    Reducers.service,
  ),
  serviceTerms: persistReducer<ReturnType<typeof Reducers.serviceTerms>>(
    {
      key: 'serviceTerms',
      stateReconciler: autoMergeLevel2,
      storage,
      timeout: 50,
    },
    Reducers.serviceTerms,
  ),
  userQuiz: persistReducer<ReturnType<typeof Reducers.userQuiz>>(
    {
      blacklist: ['isSendUserTestToUserFinished'],
      key: 'userQuiz',
      stateReconciler: autoMergeLevel2,
      storage,
      timeout: 50,
    },
    Reducers.userQuiz,
  ),
  users: persistReducer<ReturnType<typeof Reducers.users>>(
    {
      blacklist: isApplicationKiosk ? ['scanner', 'characters', 'profile'] : ['anonymousUser'],
      key: 'users',
      stateReconciler: autoMergeLevel2,
      storage,
      timeout: 50,
    },
    Reducers.users,
  ),
  widgetInstance: persistReducer<ReturnType<typeof Reducers.widgetInstance>>(
    {
      key: 'widgetInstance',
      stateReconciler: autoMergeLevel2,
      storage,
      timeout: 50,
      whitelist: ['productInteractionVariant'],
    },
    Reducers.widgetInstance,
  ),
});

export const createMockedStore = (mockedState = {}) =>
  configureStore({
    preloadedState: mockedState,
    reducer: Reducer,
  });

// ! Need to ensure import order in main.tsx
export const store = ENV_IS_TESTING
  ? createMockedStore(initialMockedState)
  : configureStore({
      middleware: getDefaultMiddleware =>
        getDefaultMiddleware({
          serializableCheck: {
            ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
          },
        }).concat(analyticsMiddleware),
      reducer: Reducer,
    });

persistStore(store);
